import { HeaderMenuAction, HeaderMenuSection } from 'components/PageLayout';
import { CameraTimeLapseFrameDetails } from 'features/cameras/cameras.types';
import { TFunction } from 'i18next';
import { UseMapState } from 'state/useMapState';
import { hasValidCoordinates } from 'shared/utils';
import { GeoEventTypes } from '../../../constants';
import * as wildfireIcons from '../../../features/mapping/icons/iconData/wildfireIcons';
import { ActiveFloodIcon } from '../../../features/mapping/icons/iconData/floodingIcons';

const buildCreateLinkWithParent = (
  type: 'location' | 'hazard',
  parentGeoEventId: number,
  parentGeoEventType: 'wildfire' | 'flooding',
  geoEventMapState: { lat: number; lng: number } | null,
): string => {
  const params = new URLSearchParams({
    parentGeoEventId: parentGeoEventId.toString(),
    parentGeoEventType: parentGeoEventType.toString(),
  });

  if (geoEventMapState && hasValidCoordinates(geoEventMapState)) {
    params.append('lat', geoEventMapState.lat.toString());
    params.append('lng', geoEventMapState.lng.toString());
  }

  return `/${type}/create?${params.toString()}`;
};

export const getIncidentPageMenuItems = (
  t: TFunction,
  data: {
    automatedGeoEvent: boolean;
    geoEventId: number;
    geoEventMapState: {
      lat: number;
      lng: number;
    } | null;
    canCreateLocation: boolean;
    canReportHazard: boolean;
    isPrescribed: boolean;
    isAddUpdateDisabled: boolean;
    isEditIncidentDisabled: boolean;
  },
): HeaderMenuSection[] => {
  const {
    automatedGeoEvent,
    geoEventId,
    geoEventMapState,
    canCreateLocation,
    canReportHazard,
    isPrescribed,
    isAddUpdateDisabled,
    isEditIncidentDisabled,
  } = data;

  const routePrefix = automatedGeoEvent ? 'i' : 'incident';
  const editLink = `/${routePrefix}/${geoEventId}/edit`;
  const editIncidentAction: HeaderMenuAction = {
    label: t('header.actions.editIncident'),
    to: editLink,
    keyboardShortcut: 'e',
    disabled: isEditIncidentDisabled,
    tooltip: isEditIncidentDisabled
      ? t('header.actions.editIncidentDisabledTooltip')
      : undefined,
  };

  const incidentItems: HeaderMenuAction[] = [];

  const sections: HeaderMenuSection[] = [
    { label: t('header.sections.incident'), items: incidentItems },
  ];

  // Automated incidents: single edit action
  if (automatedGeoEvent) {
    incidentItems.push(editIncidentAction);
    return sections;
  }

  incidentItems.push(
    {
      label: t('header.actions.addUpdate'),
      to: `/add_report/${geoEventId}`,
      keyboardShortcut: 'c u',
      disabled: isAddUpdateDisabled,
      tooltip: isAddUpdateDisabled
        ? t('header.actions.addUpdateDisabledTooltip')
        : undefined,
    },
    editIncidentAction,
  );

  // Prescribed burns have no "other" actions
  if (isPrescribed) {
    return sections;
  }

  const otherItems: HeaderMenuAction[] = [];

  if (canCreateLocation) {
    otherItems.push({
      label: t('header.actions.addLocation'),
      to: buildCreateLinkWithParent(
        'location',
        geoEventId,
        GeoEventTypes.wildfire,
        geoEventMapState,
      ),
      keyboardShortcut: 'c l',
    });
  }

  if (canReportHazard) {
    otherItems.push({
      label: t('header.actions.addHazard'),
      to: buildCreateLinkWithParent(
        'hazard',
        geoEventId,
        GeoEventTypes.wildfire,
        geoEventMapState,
      ),
      keyboardShortcut: 'c h',
    });
  }

  if (otherItems.length > 0) {
    sections.push({ label: t('header.sections.other'), items: otherItems });
  }

  return sections;
};

export const getLocationPageMenuItems = (
  t: TFunction,
  locationId: number,
  canCreateLocation: boolean,
): HeaderMenuSection[] => {
  if (!canCreateLocation) {
    return [];
  }

  return [
    {
      items: [
        {
          label: t('header.actions.editLocation'),
          to: `/location/${locationId}/edit`,
          keyboardShortcut: 'e',
        },
      ],
    },
  ];
};

export const getHazardPageMenuItems = (
  t: TFunction,
  hazardId: number,
  canReportHazard: boolean,
): HeaderMenuSection[] => {
  if (!canReportHazard) {
    return [];
  }

  return [
    {
      items: [
        {
          label: t('header.actions.addUpdate'),
          to: `/hazard/${hazardId}/reports/create`,
          keyboardShortcut: 'c u',
        },
        {
          label: t('header.actions.editHazard'),
          to: `/hazard/${hazardId}/edit`,
          keyboardShortcut: 'e',
        },
      ],
    },
  ];
};

export const getFloodingPageMenuItems = (
  t: TFunction,
  data: {
    floodingId: number;
    isRxOnlyReporter: boolean;
    geoEventMapState: {
      lat: number;
      lng: number;
    } | null;
    canCreateLocation: boolean;
    canReportHazard: boolean;
  },
): HeaderMenuSection[] => {
  const {
    floodingId,
    isRxOnlyReporter,
    geoEventMapState,
    canCreateLocation,
    canReportHazard,
  } = data;

  if (isRxOnlyReporter) {
    return [];
  }

  const incidentItems: HeaderMenuAction[] = [
    {
      label: t('header.actions.addUpdate'),
      to: `/flooding/${floodingId}/reports/create`,
      keyboardShortcut: 'c u',
    },
    {
      label: t('header.actions.editIncident'),
      to: `/flooding/${floodingId}/edit`,
      keyboardShortcut: 'e',
    },
  ];

  const otherItems: HeaderMenuAction[] = [];

  if (canCreateLocation) {
    otherItems.push({
      label: t('header.actions.addLocation'),
      to: buildCreateLinkWithParent(
        'location',
        floodingId,
        GeoEventTypes.flooding,
        geoEventMapState,
      ),
      keyboardShortcut: 'c l',
    });
  }

  if (canReportHazard) {
    otherItems.push({
      label: t('header.actions.addHazard'),
      to: buildCreateLinkWithParent(
        'hazard',
        floodingId,
        GeoEventTypes.flooding,
        geoEventMapState,
      ),
      keyboardShortcut: 'c h',
    });
  }

  const sections: HeaderMenuSection[] = [
    { label: t('header.sections.incident'), items: incidentItems },
  ];

  if (otherItems.length > 0) {
    sections.push({ label: t('header.sections.other'), items: otherItems });
  }

  return sections;
};

export const getDefaultMenuItems = (
  t: TFunction,
  data: {
    incidentMapState: UseMapState['incidentMapState'];
    timelapseFrame: CameraTimeLapseFrameDetails | null;
    cameraId?: string;
    canReportWildfire: boolean;
    canReportFlooding: boolean;
    canCreateLocation: boolean;
    canReportHazard: boolean;
    isRxOnlyReporter: boolean;
  },
): HeaderMenuSection[] => {
  const {
    incidentMapState,
    timelapseFrame,
    cameraId,
    canReportWildfire,
    canReportFlooding,
    canCreateLocation,
    canReportHazard,
    isRxOnlyReporter,
  } = data;

  let createWildfire = '/incident/create';
  let createPrescribedBurn = '/incident/create';
  let createLocation = '/location/create';
  let createFlooding = '/flooding/create';
  let createHazard = '/hazard/create';

  if (hasValidCoordinates(incidentMapState.center)) {
    const params = new URLSearchParams({
      lat: incidentMapState.center.lat.toString(),
      lng: incidentMapState.center.lng.toString(),
    });

    if (incidentMapState.zoom) {
      params.append('zoom', incidentMapState.zoom.toString());
    }
    createLocation += `?${params.toString()}`;
    if (cameraId) {
      params.append('alertCameraId', cameraId);
      if (timelapseFrame?.imageTimestamp) {
        params.append('alertCameraTs', timelapseFrame.imageTimestamp);
      }
    }
    createHazard += `?${params.toString()}`;
    createWildfire += `?${params.toString()}`;
    createFlooding += `?${params.toString()}`;

    const prescribedParams = new URLSearchParams(params);
    prescribedParams.append('prescribed', 'true');
    createPrescribedBurn += `?${prescribedParams.toString()}`;
  } else {
    createPrescribedBurn += '?prescribed=true';
  }

  const incidentItems: HeaderMenuAction[] = [];

  if (canReportWildfire) {
    if (!isRxOnlyReporter) {
      incidentItems.push({
        label: t('header.actions.addWildfire'),
        to: createWildfire,
        keyboardShortcut: 'c i',
        iconSrc: wildfireIcons.ActiveFireIcon.data,
      });
    }

    incidentItems.push({
      label: t('header.actions.addPrescribedBurn'),
      to: createPrescribedBurn,
      keyboardShortcut: 'c r',
      iconSrc: wildfireIcons.ActivePrescribedFireIcon.data,
    });
  }

  if (canReportFlooding && !isRxOnlyReporter) {
    incidentItems.push({
      label: t('header.actions.addFlooding'),
      to: createFlooding,
      keyboardShortcut: 'c f',
      iconSrc: ActiveFloodIcon.data,
    });
  }

  const sections: HeaderMenuSection[] = [];

  if (incidentItems.length > 0) {
    sections.push({
      label: t('header.sections.incident'),
      items: incidentItems,
    });
  }

  // RxOnly: flat list without sections
  if (isRxOnlyReporter) {
    return sections;
  }

  const otherItems: HeaderMenuAction[] = [];

  if (canCreateLocation) {
    otherItems.push({
      label: t('header.actions.addLocation'),
      to: createLocation,
      keyboardShortcut: 'c l',
    });
  }

  if (canReportHazard) {
    otherItems.push({
      label: t('header.actions.addHazard'),
      to: createHazard,
      keyboardShortcut: 'c h',
    });
  }

  if (otherItems.length > 0) {
    sections.push({ label: t('header.sections.other'), items: otherItems });
  }

  return sections;
};

const getAlertPageMenuItems = (
  t: TFunction,
  alertId: number,
): HeaderMenuSection[] => [
  {
    items: [
      {
        label: t('header.actions.editAlert'),
        to: `/alert/${alertId}/edit`,
        keyboardShortcut: 'e',
      },
    ],
  },
];

export const getAdminMenuItems = (
  t: TFunction,
  data: {
    automatedGeoEvent: boolean;
    geoEventId?: number;
    locationId?: number;
    floodingId?: number;
    hazardId?: number;
    alertId?: number;
    geoEventMapState: {
      lat: number;
      lng: number;
    } | null;
    canReportWildfire: boolean;
    canReportFlooding: boolean;
    canCreateLocation: boolean;
    canReportHazard: boolean;
    incidentMapState: UseMapState['incidentMapState'];
    timelapseFrame: CameraTimeLapseFrameDetails | null;
    cameraId?: string;
    isRxOnlyReporter: boolean;
    isPrescribed: boolean;
    isAddUpdateDisabled: boolean;
    isEditIncidentDisabled: boolean;
  },
): HeaderMenuSection[] => {
  const {
    automatedGeoEvent,
    geoEventId,
    locationId,
    floodingId,
    hazardId,
    alertId,
    geoEventMapState,
    canReportWildfire,
    canReportFlooding,
    canCreateLocation,
    canReportHazard,
    incidentMapState,
    isPrescribed,
    timelapseFrame,
    cameraId,
    isRxOnlyReporter,
    isAddUpdateDisabled,
    isEditIncidentDisabled,
  } = data;

  if (geoEventId) {
    return getIncidentPageMenuItems(t, {
      automatedGeoEvent,
      geoEventId,
      geoEventMapState,
      canCreateLocation,
      canReportHazard: false,
      isPrescribed,
      isAddUpdateDisabled,
      isEditIncidentDisabled,
    });
  }

  if (locationId) {
    return getLocationPageMenuItems(t, locationId, canCreateLocation);
  }

  if (floodingId) {
    return getFloodingPageMenuItems(t, {
      floodingId,
      isRxOnlyReporter,
      geoEventMapState,
      canCreateLocation,
      canReportHazard,
    });
  }

  if (hazardId) {
    return getHazardPageMenuItems(t, hazardId, canReportHazard);
  }

  if (alertId) {
    return getAlertPageMenuItems(t, alertId);
  }

  return getDefaultMenuItems(t, {
    incidentMapState,
    timelapseFrame,
    cameraId,
    canReportWildfire,
    canReportFlooding,
    canCreateLocation,
    canReportHazard,
    isRxOnlyReporter,
  });
};
